import React from 'react';

import { PaddingModes } from '@wix/wix-ui-tpa';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { withErrorBoundary } from '@/errors';
import { assertNonNullable } from '@/errors/assertions';

import { PayloadAction, PayloadCmpProps } from '../ChatMessagePayload/types';
import { LinkButton, WidgetButtonPriority } from '../WidgetButton';

import { classes, st } from './PagePayload.st.css';

type PagePayloadProps = PayloadCmpProps & {
  image?: string;
  title?: string;
  link?: string;
};

export const PagePayload: React.FC<
  PagePayloadProps & React.HTMLAttributes<HTMLElement>
> = withErrorBoundary(
  ({
    isSmall,
    isMultiple,
    isProductsWithOneSitePage,
    image,
    title,
    link,
    actions,
    payloadId,
    ...rootElementAttrs
  }) => {
    assertNonNullable(
      link,
      () => new MissingLinkError(`Page payload with no link! ${title} ${link}`),
    );

    const { t } = useTranslation();

    return (
      <div
        className={`${st(classes.root)} ${rootElementAttrs.className}`}
        aria-label={t('app.widget.ariallabel.page')}
      >
        <LinkButton
          priority={
            isMultiple || isProductsWithOneSitePage
              ? WidgetButtonPriority.secondary
              : WidgetButtonPriority.primary
          }
          title={title ?? link}
          link={link}
          reportNavigationToAssistant={
            payloadId && actions?.includes(PayloadAction.VIEW)
              ? { id: payloadId }
              : undefined
          }
          // This prop is needed for ellipsis behavior
          paddingMode={PaddingModes.explicitPaddings}
        />
      </div>
    );
  },
  (error) => {
    if (error instanceof MissingLinkError) {
      console.error(error.message);
      return null;
    }
    throw error;
  },
);

class MissingLinkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MissingLinkError';
  }
}
